// A19 — Huquqiy so'rovlar (legal requests) sub-flow ekranlari. Module-local
// data-qatlam: `lib/adapters.ts` va `i18n/*` ga TEGMAYDI (ular boshqa session'da
// HOT). Read-only ko'rinishlar HAQIQIY pipeline ustidan:
//   • GET /admin/legal          → huquqiy so'rov (kind/status/jurisdiction)
//   • GET /admin/gdpr-requests  → GDPR subyekt so'rovi (access/export/erasure)
// Ko'p ekran bitta legal REQUEST ning pastki-bosqichi — shu sabab jadval/read-only.
// Barcha fetcher FAIL-SOFT: backend yo'q/xato bo'lsa bo'sh ro'yxat (ekran ochiladi).
// Registratsiya (App.tsx route / screenStatus / nav / i18n) = task RETURN bloki
// (C qo'llaydi) — bu yerda HECH QACHON o'sha global fayllar tahrirlanmaydi.
import { api } from '@/lib/api'
import type { EnumOption, FetchParams, FetchResult } from '@/components/DataTable/types'

function toResult<T>(rows: T[], params: FetchParams, search?: (r: T) => string): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q && search ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = params.page * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

async function safeList<T>(path: string, key: string, signal: AbortSignal): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, { signal, query: { limit: 300 } })
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

/** A19.03–A19.11 — GET /admin/legal dan huquqiy so'rov (admin-only). */
export interface LegalRow {
  id: number
  kind: string
  authority: string
  reference?: string | null
  jurisdiction?: string | null
  affected_user_id?: number | null
  username?: string | null
  status: string
  details?: string | null
  resolution?: string | null
  created_at: number
  resolved_at?: number | null
}

/** A19.15/16 — GET /admin/gdpr-requests dan GDPR subyekt so'rovi. */
export interface GdprRow {
  id: number
  subject: string
  kind: string
  status: string
  reason: string
  resolution: string
  due_at: string
  completed_at: string | null
  created_at: string
  overdue: boolean
}

const legal = (signal: AbortSignal) => safeList<LegalRow>('/admin/legal', 'requests', signal)
const gdpr = (signal: AbortSignal) => safeList<GdprRow>('/admin/gdpr-requests', 'requests', signal)

const legalSearch = (r: LegalRow) => `${r.authority} ${r.reference ?? ''} ${r.jurisdiction ?? ''} ${r.kind} ${r.username ?? ''}`
const gdprSearch = (r: GdprRow) => `${r.subject} ${r.reason} ${r.kind}`

/** A19.03 — Huquqiy asos tekshiruvi: har so'rovning asosi (tur/organ/referens/yurisdiksiya). */
export function fetchLegalBasis(params: FetchParams): Promise<FetchResult<LegalRow>> {
  return legal(params.signal).then((rows) => toResult(rows, params, legalSearch))
}

/** A19.04 — Yurisdiksiya: so'rovlar yurisdiksiya kesimida (yurisdiksiya bo'yicha tartib). */
export function fetchJurisdictions(params: FetchParams): Promise<FetchResult<LegalRow>> {
  return legal(params.signal).then((rows) =>
    toResult([...rows].sort((a, b) => (a.jurisdiction ?? '￿').localeCompare(b.jurisdiction ?? '￿')), params, legalSearch),
  )
}

/** A19.08 — Bloklash talablari: kind=blocking. */
export function fetchBlockingDemands(params: FetchParams): Promise<FetchResult<LegalRow>> {
  return legal(params.signal).then((rows) => toResult(rows.filter((r) => r.kind === 'blocking'), params, legalSearch))
}

/** A19.09 — Ma'lumot to'plash: kind=data (data-so'rovni bajarish uchun to'plash bosqichi). */
export function fetchDataCollection(params: FetchParams): Promise<FetchResult<LegalRow>> {
  return legal(params.signal).then((rows) => toResult(rows.filter((r) => r.kind === 'data'), params, legalSearch))
}

/** A19.10 — Javob tayyorlash: hali yopilmagan (pending) so'rovlar — qoralama bosqichi. */
export function fetchResponsePrep(params: FetchParams): Promise<FetchResult<LegalRow>> {
  return legal(params.signal).then((rows) => toResult(rows.filter((r) => r.status === 'pending'), params, legalSearch))
}

/** A19.11 — Yuborish va tasdiqlash: bajarilgan (fulfilled) — javob yuborilgan/tasdiqlangan. */
export function fetchSendConfirm(params: FetchParams): Promise<FetchResult<LegalRow>> {
  return legal(params.signal).then((rows) => toResult(rows.filter((r) => r.status === 'fulfilled'), params, legalSearch))
}

/** A19.15 — Ma'lumot eksporti (subyekt so'rovi): GDPR access/export. */
export function fetchDataExport(params: FetchParams): Promise<FetchResult<GdprRow>> {
  return gdpr(params.signal).then((rows) =>
    toResult(rows.filter((r) => r.kind === 'export' || r.kind === 'access'), params, gdprSearch),
  )
}

/** A19.16 — "Unutilish huquqi" so'rovlari: GDPR erasure. */
export function fetchErasure(params: FetchParams): Promise<FetchResult<GdprRow>> {
  return gdpr(params.signal).then((rows) => toResult(rows.filter((r) => r.kind === 'erasure'), params, gdprSearch))
}

/* ── enum ustun variantlari ──────────────────────────────────────── */

export const LEGAL_KIND_OPTS: EnumOption[] = [
  { value: 'data', label: 'Данные', status: 'review' },
  { value: 'preservation', label: 'Сохранение', status: 'pending' },
  { value: 'removal', label: 'Удаление контента', status: 'review' },
  { value: 'blocking', label: 'Блокировка', status: 'banned' },
  { value: 'emergency', label: 'Экстренный', status: 'banned' },
]

export const LEGAL_STATUS_OPTS: EnumOption[] = [
  { value: 'pending', label: 'В ожидании', status: 'pending' },
  { value: 'fulfilled', label: 'Исполнено', status: 'active' },
  { value: 'rejected', label: 'Отклонено', status: 'deleted' },
]

export const GDPR_KIND_OPTS: EnumOption[] = [
  { value: 'access', label: 'Доступ', status: 'review' },
  { value: 'export', label: 'Экспорт', status: 'review' },
  { value: 'erasure', label: 'Удаление', status: 'banned' },
  { value: 'rectification', label: 'Исправление', status: 'pending' },
  { value: 'objection', label: 'Возражение', status: 'pending' },
]

export const GDPR_STATUS_OPTS: EnumOption[] = [
  { value: 'open', label: 'Открыт', status: 'open' },
  { value: 'in_progress', label: 'В работе', status: 'review' },
  { value: 'completed', label: 'Выполнен', status: 'active' },
  { value: 'rejected', label: 'Отклонён', status: 'rejected' },
]

/** Module-local RU satrlar (i18n HOT → keyга ko'chiriladi). */
export const LEGAL_STR = {
  // umumiy ustunlar
  id: 'ID',
  created: 'Создан',
  kind: 'Тип',
  authority: 'Орган',
  reference: 'Референс',
  jurisdiction: 'Юрисдикция',
  affected: 'Пользователь',
  status: 'Статус',
  resolution: 'Решение',
  resolvedAt: 'Закрыт',
  subject: 'Субъект',
  reason: 'Причина',
  due: 'Срок',
  overdue: 'Просрочен',
  // A19.03
  basisTitle: 'Проверка правового основания',
  basisDesc: 'Правовое основание каждого запроса — тип, орган, референс, юрисдикция',
  // A19.04
  jurisdictionTitle: 'Юрисдикция',
  jurisdictionDesc: 'Запросы в разрезе юрисдикции и запрашивающего органа',
  // A19.08
  blockingTitle: 'Требования о блокировке',
  blockingDesc: 'Юридические требования о блокировке контента или аккаунтов',
  // A19.09
  collectionTitle: 'Сбор данных',
  collectionDesc: 'Запросы на данные — этап сбора для подготовки ответа',
  // A19.10
  responseTitle: 'Подготовка ответа',
  responseDesc: 'Запросы в ожидании — готовится черновик ответа',
  // A19.11
  sendTitle: 'Отправка и подтверждение',
  sendDesc: 'Исполненные запросы — ответ отправлен и подтверждён',
  // A19.15
  exportTitle: 'Экспорт данных (запрос субъекта)',
  exportDesc: 'GDPR: доступ и экспорт данных субъекта — учёт (срок 30 дней)',
  // A19.16
  erasureTitle: 'Право на забвение',
  erasureDesc: 'GDPR: запросы на удаление персональных данных (право на забвение)',
} as const
